import React from "react";
import { Link } from "react-router-dom";
import CTABand from "@/components/CTABand";
import SectionLabel from "@/components/SectionLabel";
import PhotoPlaceholder from "@/components/PhotoPlaceholder";
import Crown from "@/components/Crown";
import ASSETS from "@/lib/assets";

const ABOUT_MARQUEE = [
  "IDENTITY COACH",
  "TRUTH TELLER",
  "GRIT™ FRAMEWORK",
  "STOP PERFORMING",
  "RETURN TO YOURSELF",
  "HIGH-PERFORMING WOMEN",
];

const AboutMarquee = () => {
  const items = [...ABOUT_MARQUEE, ...ABOUT_MARQUEE, ...ABOUT_MARQUEE, ...ABOUT_MARQUEE];
  return (
    <div className="marquee" data-testid="about-marquee">
      <div className="marquee-track">
        {items.map((p, i) => (
          <React.Fragment key={i}>
            <span>{p}</span>
            <span className="sep">✦</span>
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

const BELIEFS = [
  "Every woman has a true identity beneath the roles she performs.",
  "Disconnection is not a character flaw — it’s a pattern that can be interrupted.",
  "Clarity comes from honesty — not hustle.",
  "The goal is alignment — not perfection.",
  "Identity work is the most practical investment a high-performing woman can make.",
  "You don’t need permission to become who you already are.",
];

const PHILOSOPHY = [
  "Identity is the foundation — not a side project.",
  "Clarity doesn’t come from thinking harder. It comes from telling the truth.",
  "The performance is the problem — not the solution.",
  "You don’t need permission to become who you already are.",
  "Real confidence is built on self-trust — not external proof.",
  "The goal isn’t to be more. It’s to stop hiding what’s already there.",
];

const LOUNGE_PERKS = [
  {
    h: "Real Conversations",
    p: "Honest, unfiltered discussions about identity, burnout, and becoming that you can’t have anywhere else.",
  },
  {
    h: "Women Walking the Same Path",
    p: "High-performing women who understand the specific kind of lonely that comes with success that doesn’t feel like yours.",
  },
  {
    h: "Access to Nijah",
    p: "Direct access to Nijah’s insights, resources, and occasional live sessions — free.",
  },
  {
    h: "First Access to Everything",
    p: "New programs, freebies, and opportunities to work together — community members hear first.",
  },
];

const About = () => (
  <div data-testid="page-about">
    {/* HERO */}
    <section className="hero">
      <div className="hero-grid">
        <div className="hero-content">
          <SectionLabel gold>ABOUT NIJAH ALMOND</SectionLabel>
          <h1>
            Identity coach. <em>Truth teller.</em>
            <br />
            <span style={{ color: "#fff" }}>Permission giver.</span>
          </h1>
          <p className="hero-tagline">
            “I don’t help women find their purpose. I help them stop performing
            and tell the truth.”
          </p>
          <p style={{
            fontSize: 11, letterSpacing: 2.5, textTransform: "uppercase",
            color: "var(--coral)", marginTop: -8, fontWeight: 500,
          }}>
            — Nijah Almond
          </p>
          <div className="hero-cta-row">
            <Link to="/contact" className="btn btn-primary" data-testid="about-apply">
              Apply to Work With Nijah →
            </Link>
            <Link to="/programs" className="btn btn-ghost" data-testid="about-programs-cta">
              View Programs
            </Link>
          </div>
          <img
            src={ASSETS.wordmark}
            alt="Nijah Almond"
            style={{ width: 180, height: "auto", marginTop: 18, filter: "brightness(1.1)" }}
          />
        </div>
        <div className="hero-photo">
          <PhotoPlaceholder src={ASSETS.photoBW} position="center 25%" label="Editorial portrait" />
        </div>
      </div>
    </section>

    <AboutMarquee />

    {/* HOW THIS WORK BEGAN */}
    <section className="section-pad bg-cream">
      <div className="container-x" style={{ maxWidth: 760 }}>
        <SectionLabel>HOW THIS WORK BEGAN</SectionLabel>
        <h2 className="section-title text-teal">
          The breakdown that <span className="italic-c text-coral">became the turning point.</span>
        </h2>
        <p className="body-base">
          I spent years building a life that looked right but felt wrong. I was
          high-performing, well-regarded — and completely disconnected from
          myself.
        </p>
        <p className="body-base">
          The turning point wasn’t a breakthrough. It was a breakdown. I had to
          stop performing long enough to ask:{" "}
          <em>who am I when I’m not proving something?</em>
        </p>
        <p className="body-base">
          That question changed everything. And it’s the question I now help
          other women answer — not with affirmations or vision boards, but with
          honest, structured identity work that gets to the root of the
          disconnect.
        </p>

        <p className="italic-c" style={{
          fontSize: 22, lineHeight: 1.45, color: "var(--teal)",
          padding: "8px 0 8px 22px",
          borderLeft: "2px solid var(--coral)",
          margin: "26px 0",
        }}>
          The work isn’t about finding something new. It’s about uncovering
          what’s been there all along.
        </p>

        <p className="body-base">
          The GRIT™ Identity Transformation System was born from this
          experience — a framework to help high-performing women stop running
          from themselves and start returning to who they actually are.
        </p>
        <Link
          to="/programs#grit"
          className="btn btn-outline-dark"
          style={{ marginTop: 20 }}
          data-testid="about-explore-grit"
        >
          Explore the GRIT™ System →
        </Link>
      </div>
    </section>

    {/* MY BELIEF (split + pull quote) */}
    <section className="section-pad bg-teal">
      <div className="container-x">
        <div className="split">
          <div>
            <SectionLabel gold>MY BELIEF</SectionLabel>
            <h2 className="section-title" style={{ color: "#fff" }}>
              Most women don’t need <span className="italic-c text-gold">more strategy.</span>
            </h2>
            <p className="body-base" style={{ color: "rgba(255,255,255,.88)" }}>
              They need someone to help them see the identity pattern running
              their decisions — and a framework to rebuild from truth instead
              of performance.
            </p>
            <p className="body-base" style={{ color: "rgba(255,255,255,.88)" }}>
              Most women I work with aren’t lost. They’re buried under a version
              of themselves they built to survive. My work interrupts that pattern.
            </p>
            <Link
              to="/coaching"
              className="btn btn-primary"
              style={{ marginTop: 18 }}
              data-testid="about-explore-1on1"
            >
              Explore 1:1 Coaching →
            </Link>
          </div>
          <div style={{
            background: "rgba(255,255,255,.04)",
            border: "1px solid rgba(243,178,26,.4)",
            padding: "36px 32px",
            borderRadius: 2,
          }}>
            <Crown size={36} opacity={0.95} />
            <p className="italic-c" style={{
              fontSize: 22, color: "#fff", lineHeight: 1.45, marginTop: 16,
            }}>
              “Most women I work with aren’t lost — they’re buried under a
              version of themselves they built to survive. The work isn’t about
              finding something new. It’s about uncovering what’s been there all along.”
            </p>
            <p style={{
              fontSize: 11, letterSpacing: 2, textTransform: "uppercase",
              color: "var(--coral)", marginTop: 18,
            }}>
              — Nijah Almond
            </p>
          </div>
        </div>
      </div>
    </section>

    {/* OUR MISSION */}
    <section className="mirror" style={{ background: "var(--blush)" }}>
      <div className="mirror-inner">
        <SectionLabel center>OUR MISSION</SectionLabel>
        <p className="mirror-quote">
          “To help high-performing women stop performing — and start{" "}
          <span className="italic-c">becoming.</span>”
        </p>
        <p className="body-base" style={{ maxWidth: 620, margin: "12px auto 0" }}>
          We believe identity is the foundation of every meaningful decision.
          When you know who you are, everything gets clearer — your career,
          your voice, your boundaries, your life.
        </p>
      </div>
    </section>

    {/* WHAT WE BELIEVE — 6 numbered convictions */}
    <section className="section-pad bg-teal">
      <div className="container-x">
        <SectionLabel gold>WHAT WE BELIEVE</SectionLabel>
        <h2 className="section-title" style={{ color: "#fff", marginBottom: 28 }}>
          The convictions that <span className="italic-c text-gold">drive every session.</span>
        </h2>
        <div className="conv-grid" data-testid="convictions-grid">
          {BELIEFS.map((b, i) => (
            <div className="conv-card" key={i}>
              <span className="conv-num">{String(i + 1).padStart(2, "0")}</span>
              <p>{b}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* PHILOSOPHY — 6 numbered principles */}
    <section className="section-pad bg-cream">
      <div className="container-x">
        <div className="split">
          <div>
            <SectionLabel>PHILOSOPHY</SectionLabel>
            <h2 className="section-title text-teal">
              The principles behind <span className="italic-c text-coral">every conversation.</span>
            </h2>
            <p className="body-base">
              This work is built on core convictions that guide how Nijah
              coaches, what she challenges, and how she holds space for every
              woman in her world.
            </p>
          </div>
          <ol className="num-list">
            {PHILOSOPHY.map((p, i) => (
              <li key={i}>{p}</li>
            ))}
          </ol>
        </div>
      </div>
    </section>

    {/* LEGACY LOUNGE */}
    <section className="lounge" data-testid="legacy-lounge">
      <div className="container-x">
        <div className="split">
          <div>
            <SectionLabel gold>FREE COMMUNITY</SectionLabel>
            <h2 className="section-title" style={{ color: "var(--cream)" }}>
              Join The <span className="italic-c text-gold">Legacy Lounge.</span>
            </h2>
            <p className="body-base" style={{ color: "rgba(253,248,245,.78)" }}>
              A room full of women walking this same path — navigating growth,
              identity shifts, purpose… and the in-between moments no one talks
              about but everyone feels.
            </p>
            <p className="body-base" style={{ color: "rgba(253,248,245,.78)" }}>
              This is where the real conversations happen. The honest ones. The
              ones that change you.
            </p>
            <p className="italic-c" style={{
              color: "var(--gold)", fontSize: 20, marginTop: 14,
            }}>
              I’ll see you inside The Legacy Lounge —
            </p>
            <a
              href="https://www.skool.com/the-legacy-lounge-free-7046/about?ref=4aecf50ea0e64eb0924b177d6890a1fb"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary"
              style={{ marginTop: 20 }}
              data-testid="legacy-lounge-cta"
            >
              Join the Community — It’s Free →
            </a>
          </div>
          <div className="perk-cards">
            {LOUNGE_PERKS.map((p, i) => (
              <div key={i}>
                <h5>{p.h}</h5>
                <p>{p.p}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    <CTABand
      eyebrow="READY TO DO THE WORK?"
      title={<>The first step is <span className="italic-c text-gold">the honest one.</span></>}
      sub="Whether you start with the free community, a free resource, or a complimentary visionary call — the work begins the moment you stop performing."
      primaryLabel="Apply Now →"
      primaryTo="/contact"
      secondaryLabel="Join The Legacy Lounge — Free"
      secondaryTo="/about#legacy-lounge"
    />
  </div>
);

export default About;
